"use client"

import { Link, useLocation } from "react-router-dom"
import { BarChart, MessageSquare, Settings, Home, LogOut, Users, Shield } from "lucide-react"
import { 
  prefetchDashboard, 
  prefetchConversations, 
  prefetchReports, 
  prefetchAdmin,
  prefetchCustomers,
  prefetchPlatformAdmin,
  prefetchProfile
} from "@/utils/route-prefetch"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/providers/auth-provider"
import { CompanySwitcher } from "@/components/company-switcher"
import { AppSwitcher } from "@/components/app-switcher"
import { canAccessAdmin, isPlatformAdmin } from "@/utils/auth-helpers"

export function AppSidebar() {
  const location = useLocation()
  const pathname = location.pathname
  const { logout, user } = useAuth()
  const { toggleSidebar } = useSidebar()
  const showAdmin = canAccessAdmin(user)
  const showPlatformAdmin = isPlatformAdmin(user)

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center justify-between p-2 group-data-[collapsible=icon]:justify-center">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={toggleSidebar}
              className="cursor-pointer"
              aria-label="Toggle sidebar"
            >
              <MessageSquare className="h-6 w-6 shrink-0" />
            </button>
            <span className="text-lg font-semibold truncate group-data-[collapsible=icon]:hidden">MessageLink</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <div className="px-4 pb-4 space-y-3 group-data-[collapsible=icon]:hidden">
          <CompanySwitcher />
          <AppSwitcher />
        </div>

        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={pathname === "/dashboard"} tooltip="Dashboard">
              <Link 
                to="/dashboard" 
                className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-full"
                onMouseEnter={prefetchDashboard}
              >
                <Home className="h-4 w-4" />
                <span className="group-data-[collapsible=icon]:hidden">Dashboard</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={pathname.startsWith("/conversations")} tooltip="Conversations">
              <Link 
                to="/conversations" 
                className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-full"
                onMouseEnter={prefetchConversations}
              >
                <MessageSquare className="h-4 w-4" />
                <span className="group-data-[collapsible=icon]:hidden">Conversations</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={pathname.startsWith("/customers")} tooltip="Customers">
              <Link 
                to="/customers" 
                className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-full"
                onMouseEnter={prefetchCustomers}
              >
                <Users className="h-4 w-4" />
                <span className="group-data-[collapsible=icon]:hidden">Customers</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={pathname.startsWith("/reports")} tooltip="Reports">
              <Link 
                to="/reports" 
                className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-full"
                onMouseEnter={prefetchReports}
              >
                <BarChart className="h-4 w-4" />
                <span className="group-data-[collapsible=icon]:hidden">Reports</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {showAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname.startsWith("/admin")} tooltip="Admin">
                <Link 
                  to="/admin" 
                  className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-full"
                  onMouseEnter={prefetchAdmin}
                >
                  <Settings className="h-4 w-4" />
                  <span className="group-data-[collapsible=icon]:hidden">Admin</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {showPlatformAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname.startsWith("/platform-admin")} tooltip="Platform Admin">
                <Link 
                  to="/platform-admin" 
                  className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-full"
                  onMouseEnter={prefetchPlatformAdmin}
                >
                  <Shield className="h-4 w-4" />
                  <span className="group-data-[collapsible=icon]:hidden">Platform Admin</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Profile">
              <Link 
                to="/profile"
                className="flex items-center gap-2 px-2 py-2 group-data-[collapsible=icon]:justify-center"
                onMouseEnter={prefetchProfile}
              >
                <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                  {user?.initials || user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
                </div>
                <div className="flex flex-col min-w-0 group-data-[collapsible=icon]:hidden text-left">
                  <span className="text-sm font-medium truncate">{user?.name || "User"}</span>
                  <span className="text-xs text-muted-foreground truncate">{user?.email || ""}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout} tooltip="Sign Out">
              <LogOut className="h-4 w-4" />
              <span className="group-data-[collapsible=icon]:hidden">Sign Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
